// src/hooks/useAutoSave.ts
import { useEffect, useRef, useState, useCallback } from 'react';

interface UseAutoSaveOptions {
  onSave: () => Promise<void> | void;
  delay?: number; // milliseconds, default 30000 (30 seconds)
  enabled?: boolean;
}

interface AutoSaveStatus {
  isSaving: boolean;
  lastSaved: Date | null;
  error: string | null;
}

/**
 * Auto-save hook with debounce
 * Automatically saves data after a delay when changes are detected
 * 
 * @example
 * const { triggerSave, status } = useAutoSave({
 *   onSave: async () => {
 *     await saveToDatabase(data);
 *   },
 *   delay: 30000, // 30 seconds
 *   enabled: true
 * });
 * 
 * // Trigger save when data changes
 * useEffect(() => {
 *   triggerSave();
 * }, [data, triggerSave]);
 */
export function useAutoSave({ onSave, delay = 30000, enabled = true }: UseAutoSaveOptions) {
  const [status, setStatus] = useState<AutoSaveStatus>({
    isSaving: false,
    lastSaved: null,
    error: null
  });

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const performSave = useCallback(async () => {
    if (!enabled || !isMountedRef.current) return;

    try {
      setStatus(prev => ({ ...prev, isSaving: true, error: null }));
      await onSave();
      
      if (isMountedRef.current) {
        setStatus({
          isSaving: false,
          lastSaved: new Date(),
          error: null
        });
      }
    } catch (error) {
      console.error('[AutoSave] Save failed:', error);
      if (isMountedRef.current) {
        setStatus(prev => ({
          ...prev,
          isSaving: false,
          error: error instanceof Error ? error.message : 'Save failed'
        }));
      }
    }
  }, [onSave, enabled]);

  const triggerSave = useCallback(() => {
    if (!enabled) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout
    saveTimeoutRef.current = setTimeout(() => {
      performSave();
    }, delay);
  }, [delay, performSave, enabled]);

  // Manual save (immediate, no debounce)
  const saveNow = useCallback(async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    await performSave();
  }, [performSave]);

  return {
    triggerSave,
    saveNow,
    status,
    isSaving: status.isSaving,
    lastSaved: status.lastSaved,
    error: status.error
  };
}

/**
 * Format last saved time for display
 */
export function formatLastSaved(lastSaved: Date | null): string {
  if (!lastSaved) return 'Not saved';

  const now = new Date();
  const diffMs = now.getTime() - lastSaved.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);

  if (diffSeconds < 10) return 'Just now';
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  
  return lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
